import { BoundMultiSelectField, BoundNumberField, BoundSelectField, BoundSelectFieldProps, Css } from "@homebound/beam";
import { FieldState, ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { ReactNode, useMemo } from "react";
import { BannerNotice, Loading } from "src/components";
import { ArchivedTag } from "src/components/ArchivedTag";
import { UnitsOfMeasureBoundSelectField } from "src/components/autoPopulateSelects/UnitsOfMeasureBoundSelectField";
import { Maybe, SaveTakeoffLineItemInput } from "src/generated/graphqlTypes";
import {
  AddEditItemModal_GlobalPlanTaskFragment,
  AddEditItemModal_ItemFragment,
  AddEditItemModal_LocationFragment,
  AddEditItemModal_MvFragment,
  AddEditItemModal_TliFragment,
  AddEditItemModal_UomFragment,
  useAddEditItemModal_GetReadyPlanQuery,
  useAddEditItemModal_GetTliQuery,
  useAddEditItemModal_GlobalPlanTasksLazyQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";
import { StoreApi } from "zustand";
import { SaveTliResult, TakeoffsStoreState } from "../../TakeoffsManagerContext";
export function PublishedTakeoffBanner() {
  return (
    <BannerNotice message="This takeoff has been published and cannot be edited" variant="form" icon="alertInfo" />
  );
}

export type AddEditItemObjectState = SaveTakeoffLineItemInput & {
  materialVariant: Maybe<AddEditItemModal_MvFragment>;
  unitOfMeasure: Maybe<AddEditItemModal_UomFragment>;
  item: Maybe<AddEditItemModal_ItemFragment>;
  location: Maybe<AddEditItemModal_LocationFragment>;
  newLocationPath?: Maybe<string>;
};

export const addItemConfig: ObjectConfig<AddEditItemObjectState> = {
  id: { type: "value" },
  itemId: { type: "value" },
  materialVariantId: { type: "value" },
  quantity: { type: "value" },
  unitOfMeasureId: { type: "value", rules: [required] },
  locationId: { type: "value", rules: [required] },
  optionIds: { type: "value" },
  taskId: { type: "value" },

  // The fully-assembled path (i.e., parent path + new feature path) which only gets set when adding a new feature
  newLocationPath: { type: "value" },
  // Also store the more detailed objects for easier access/logic needs
  location: { type: "value" },
  materialVariant: {
    type: "object",
    config: {
      id: { type: "value" },
      code: { type: "value" },
      displayName: { type: "value" },
      isArchived: { type: "value" },
      listing: { type: "value" },
      materialAttributeValues: { type: "value" },
      effectiveMADs: { type: "value" },
    },
  },
  unitOfMeasure: { type: "value", rules: [required] },
  item: {
    type: "object",
    config: {
      unitOfMeasure: { type: "value" },
      materialAttributeDimensions: {
        type: "list",
        config: {
          id: { type: "value" },
          name: { type: "value" },
          useInTakeoff: { type: "value" },
          unitOfMeasure: { type: "value" },
          values: {
            type: "list",
            config: {
              id: { type: "value" },
              code: { type: "value" },
              textValue: { type: "value" },
              dimension: { type: "value" },
            },
          },
          type: { type: "value" },
        },
      },
    },
  },
};

export const addMaterialItemConfig: ObjectConfig<AddEditItemObjectState> = {
  ...addItemConfig,
  materialVariantId: { type: "value", rules: [required] },
};

export const addPlaceholderItemConfig: ObjectConfig<AddEditItemObjectState> = {
  ...addItemConfig,
  itemId: { type: "value", rules: [required] },
};

export const addLaborItemConfig: ObjectConfig<AddEditItemObjectState> = {
  ...addItemConfig,
  taskId: { type: "value", rules: [required] },
};

type CommonFieldsProps = {
  planPackageId: string;
  planPackageVersionId: string;
  readOnly: boolean;
  formState: ObjectState<AddEditItemObjectState>;
  disabled?: { uom?: ReactNode; quantity?: ReactNode; options?: ReactNode };
};

/** Provides fields to the "Add Labor", "Add Placeholder", and "Add Construction" modals. */
export function CommonFields({
  planPackageId,
  planPackageVersionId,
  readOnly,
  formState,
  disabled,
}: CommonFieldsProps) {
  const { data: rpData } = useAddEditItemModal_GetReadyPlanQuery({
    variables: { readyPlanId: planPackageId, versionId: planPackageVersionId },
  });
  return (
    <>
      <div css={Css.df.jcsb.gap1.$}>
        <UnitsOfMeasureBoundSelectField
          label="UoM"
          field={formState.unitOfMeasureId}
          onSelect={(uomId, uom) => {
            formState.unitOfMeasureId.set(uomId);
            formState.unitOfMeasure.set(uom);
          }}
          disabled={disabled?.uom}
        />
        <BoundNumberField
          label="Qty"
          field={formState.quantity}
          disabled={
            disabled?.quantity ||
            (formState.unitOfMeasure.value &&
              !formState.unitOfMeasure.value?.useQuantity &&
              `${formState.unitOfMeasure.value!.name} does not uses Quantity`)
          }
        />
      </div>
      {/* When in 'readOnly', do not show if it is not set. Otherwise, it results in only showing a label with no value and looks odd. */}
      {(!readOnly || formState.optionIds.value?.nonEmpty) && (
        <BoundMultiSelectField
          label="Options"
          field={formState.optionIds}
          options={rpData?.readyPlan?.options ?? []}
          getOptionLabel={(o) => o.displayName + (o.active ? "" : " (Archived)")}
          getOptionValue={(o) => o.id}
          getOptionMenuLabel={(o) => <ArchivedTag active={o.active}>{o.displayName}</ArchivedTag>}
          disabled={disabled?.options}
        />
      )}
    </>
  );
}

export type SelectedTask = { id: string | undefined; name: string | undefined };

export type OnTaskSelect = BoundSelectFieldProps<
  SelectedTask | AddEditItemModal_GlobalPlanTaskFragment | null,
  string
>["onSelect"];

type TakeoffTaskBoundSelectFieldProps = {
  selectedTask?: SelectedTask | null;
  readOnly: boolean;
  taskId: FieldState<Maybe<string>>;
  onSelect?: OnTaskSelect;
  required?: boolean;
  allowNone?: boolean;
};

export function TakeoffTaskBoundSelectField(props: TakeoffTaskBoundSelectFieldProps) {
  const { selectedTask, readOnly, taskId, onSelect, required, allowNone } = props;
  const [loadTasks, { data: tasksData }] = useAddEditItemModal_GlobalPlanTasksLazyQuery({
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-only",
  });

  return (
    <BoundSelectField
      required={required}
      label="Task Allocation"
      field={taskId}
      nothingSelectedText="Please select a task"
      options={{
        current: selectedTask ?? undefined,
        load: () => loadTasks({ variables: { filter: {} } }),
        options: tasksData?.globalPlanTasks.entities,
      }}
      getOptionLabel={(o: any) => o.name}
      getOptionValue={(o: any) => o.id}
      onSelect={onSelect}
      readOnly={readOnly}
      {...(allowNone && { unsetLabel: "None" })}
    />
  );
}

export type AddItemModalProps = {
  tliId?: string;
  asNew?: boolean;
  planPackageId: string;
  takeoffsManagerStore: StoreApi<TakeoffsStoreState>;
};

// I wonder why we're omitting id...
export type TliData = Omit<AddEditItemModal_TliFragment, "id"> & { id?: string };

export type AddEditItemModalDataViewProps = {
  initialTliData?: TliData;
  planPackageId: string;
  takeoffsManagerStore: StoreApi<TakeoffsStoreState>;
} & AddItemModalProps;

/**
 * Fetches the TLI to edit for the labor/placeholder/construction material modals.
 *
 * WrappedComponent will be one of those three modals.
 */
export function withGetTliQuery(WrappedComponent: React.FC<AddEditItemModalDataViewProps>) {
  return ({ tliId, asNew, ...rest }: AddItemModalProps) => {
    const versionId = rest.takeoffsManagerStore.getState().planPackage.version.id;
    const { loading, data: tliData } = useAddEditItemModal_GetTliQuery({
      variables: { id: tliId!, versionId },
      skip: !tliId,
    });
    if (loading) return <Loading />;
    const initialTliData: TliData | undefined = tliData && {
      ...tliData.takeoffLineItem,
      // Clear the ID if we are creating a new item via duplicate
      ...(asNew ? { id: undefined } : {}),
      // Clear the materialVariantId if we are duplicating a placeholder item as the mv is driven by item and attribute values
      ...(asNew && tliData.takeoffLineItem.isPlaceholderMaterial
        ? { materialVariantId: undefined, materialVariant: undefined }
        : {}),
    };
    return <WrappedComponent {...rest} initialTliData={initialTliData} />;
  };
}

export function useAddEditItemModalFormState(
  type: "material" | "placeholder" | "labor",
  readOnly: boolean,
  // Creating new fields will
  initialTliData: TliData | undefined,
) {
  // We use slightly different formConfigs for material/placeholder/labor
  const formConfig = useMemo(
    () => ({ material: addMaterialItemConfig, placeholder: addPlaceholderItemConfig, labor: addLaborItemConfig })[type],
    [type],
  );

  return useFormState({
    config: formConfig,
    readOnly: readOnly,
    init: {
      input: initialTliData,
      map: (data) => {
        return {
          id: data.id,
          costType: data.costType,
          itemId: data.item?.id,
          item: data.item,
          name: data.name,
          materialVariant: data.materialVariant,
          materialVariantId: data.materialVariant?.id,
          quantity: data.quantity ?? 0,
          unitOfMeasure: data.unitOfMeasure,
          unitOfMeasureId: data.unitOfMeasure?.id,
          // Treat `Kitchen > Sink` as just `Kitchen`
          locationId: data.location.nonScopeLocation.id,
          location: data.location.nonScopeLocation,
          optionIds: data.options?.map((o) => o.id) ?? [],
          taskId: data.task?.id,
        };
      },
    },
  });
}

export async function saveAddEditItem(params: {
  formState: ObjectState<AddEditItemObjectState>;
  saveTli: (item: SaveTakeoffLineItemInput) => SaveTliResult;
  isPlaceholderItem?: boolean;
}) {
  const { formState, saveTli, isPlaceholderItem } = params;

  // itemId does not need to be sent, since it is derived from materialVariantId or taskId
  const {
    itemId,
    item,
    materialVariant,
    unitOfMeasure,
    quantity,
    newLocationPath,
    // Pulling out location here so that it does not end up in rest
    location: changedLocation,
    locationId,
    ...rest
  } = formState.changedValue;

  // Fall back to formState if materialVariant isn't in changedValue (e.g. pre-filled from DP flow).
  // But if itemId changed, keep undefined so we send [] to clear stale MAVs.
  const materialVariantForMavs = itemId ? materialVariant : (materialVariant ?? formState.materialVariant.value);

  const result = await saveTli({
    ...rest,
    ...(newLocationPath ? { locationPath: newLocationPath } : locationId ? { locationId } : {}),
    ...(formState.unitOfMeasure.value?.useQuantity !== false && quantity && { quantity }),
    ...(isPlaceholderItem && {
      // It's unclear why we always send itemId...
      itemId: itemId || formState.itemId.value,
      placeholderMaterialAttributeValues:
        // If the TLI already exists, and no change to itemId or MAVs, omit to preserve the existing placeholder
        formState.id.value && !materialVariant?.materialAttributeValues?.nonEmpty && !itemId
          ? undefined
          : (materialVariantForMavs?.materialAttributeValues?.map(({ id, textValue, code, dimension }) => ({
              ...(id && { id }),
              ...(code && { code }),
              textValue,
              dimensionId: dimension.id,
            })) ?? []),
    }),
  });

  // We thought we didn't need this (getting reset by the apollo cache), but the form state is staying dirty
  formState.commitChanges();

  return result;
}
